/**
 * Onboarding's *Website* step — w12b (C11, FR-A9 *"all eight templates as
 * thumbnails, Preview opening the viewer of FR-I16 in a new tab"*, T-1.22).
 *
 * **12D's pieces, not a second gallery.** The thumbnail is `TemplateThumb`
 * (the real `/_demo/<code>`, scaled), the preview is 12D's viewer at
 * `previewHref(code, profession)` opened with `target="_blank"` (I-7), and the
 * words for a template's fit are the gallery's own `professionFit`. The list
 * is the service's (`GET /site/templates`), ordered — never filtered — with
 * the firm's profession first.
 *
 * **No chips** (C24 item 6). C21 drew the gallery's `FamilyChips` here too;
 * the owner took the filter off both screens — *"this filter not required user
 * will select as he want from full list"* — so this is the full list in the
 * service's order, which since C24 item 8 leads with the owner's seven.
 *
 * Choosing is a radio group; *Continue* saves it. The service writes the
 * choice to the site as well as the profile (`adopt_template`), so what the
 * firm picks here is what its website renders.
 */

import { Alert, Badge, Button, Skeleton } from '@fiveox/ui'
import { Check, ExternalLink } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { TemplateThumb } from '../../../components/TemplateThumb'
import { type Template, galleryOrder, site } from '../../../lib/api/site'
import { previewHref, professionFit, suggestedFor } from '../../Site/Templates'

export function WebsiteStep({
  value,
  onChange,
  profession,
  disabled,
}: {
  value: string | null
  onChange: (code: string) => void
  profession: string | null
  disabled?: boolean
}) {
  const [templates, setTemplates] = useState<Template[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const body = await site.templates()
      setTemplates(galleryOrder(body.data.templates, profession ?? body.data.profession))
    } catch {
      setError('The templates could not be loaded.')
    }
  }, [profession])

  useEffect(() => {
    void load()
  }, [load])

  if (error) {
    return (
      <Alert tone="error">
        {error}{' '}
        <Button variant="link" onClick={() => void load()}>
          Try again
        </Button>
      </Alert>
    )
  }

  if (!templates) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-64 w-full rounded-2xl" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div role="radiogroup" aria-label="Website template" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {templates.map((template: Template) => {
          const chosen = value === template.code
          // w13b — for `other`, the practice-generic ones (ADR-0031 §4).
          const suggested = suggestedFor(template.profession, profession)
          return (
            <div
              key={template.code}
              data-template={template.code}
              className={`relative flex flex-col overflow-hidden rounded-2xl border bg-card transition ${
                chosen ? 'border-primary shadow-md ring-1 ring-primary' : 'border-border hover:border-primary/60'
              }`}
            >
              <label className="flex cursor-pointer flex-col">
                <input
                  type="radio"
                  name="website_template"
                  value={template.code}
                  checked={chosen}
                  disabled={disabled}
                  onChange={() => onChange(template.code)}
                  className="peer sr-only"
                />
                <span className="block border-b border-border peer-focus-visible:ring-2 peer-focus-visible:ring-ring">
                  <TemplateThumb code={template.code} profession={profession} name={template.name} />
                </span>
                <span className="flex flex-col gap-1 p-4 pb-2">
                  <span className="flex items-start justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-foreground">{template.name}</span>
                      {suggested && (
                        <Badge tone="info" size="sm">
                          {profession === 'other' ? 'Suits your practice' : 'For your profession'}
                        </Badge>
                      )}
                    </span>
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                        chosen ? 'border-primary bg-primary text-primary-foreground' : 'border-border'
                      }`}
                      aria-hidden
                    >
                      {chosen && <Check size={12} strokeWidth={3} />}
                    </span>
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {professionFit(template.profession)} · {template.trait}
                  </span>
                </span>
              </label>
              <div className="mt-auto px-4 pb-4">
                <a
                  href={previewHref(template.code, profession)}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                  aria-label={`Preview ${template.name} in a new tab`}
                >
                  Preview <ExternalLink size={14} aria-hidden />
                </a>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
